import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '../ui/map';
import { cn } from '../../lib/utils';
import { GlobeIcon, MinusIcon, NavigationArrowIcon, PlusIcon } from '../ui/icons';
import {
  clampPitch,
  DEFAULT_3D_PITCH,
  getNextBearingFromWheelDelta,
  getNextPitchFor3DToggle,
  getNextPitchFromWheelDelta,
  getShortestSignedAngleDelta,
  getNextZoomFromWheelDelta,
  is3DEnabled,
  normalizeBearing,
} from '../../lib/map-camera';

type MapCameraControlsProps = {
  className?: string;
  threeDPitch?: number;
  defaultCenter?: [number, number];
  defaultZoom?: number;
  zoomAround?: [number, number] | null;
  resetFitCoordinates?: [number, number][] | null;
  resetFitPadding?: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
};

interface CoordinateBounds {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

const RESET_FIT_EPSILON = 1e-7;
const DEFAULT_RESET_FIT_PADDING = {
  top: 90,
  right: 90,
  bottom: 160,
  left: 90,
} as const;

export function getCoordinateBounds(
  coordinates: [number, number][] | null | undefined,
): CoordinateBounds | null {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return null;

  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;

  for (const coordinate of coordinates) {
    const lng = Number(coordinate?.[0]);
    const lat = Number(coordinate?.[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  if (!Number.isFinite(minLng) || !Number.isFinite(maxLng) || !Number.isFinite(minLat) || !Number.isFinite(maxLat)) {
    return null;
  }

  return {
    minLng,
    maxLng,
    minLat,
    maxLat,
  };
}

export function buildZoomTransitionOptions(
  zoom: number,
  zoomAround: [number, number] | null | undefined,
): { zoom: number; around?: [number, number] } {
  if (!zoomAround) return { zoom };
  return {
    zoom,
    around: zoomAround,
  };
}

export function MapCameraControls({
  className,
  threeDPitch = DEFAULT_3D_PITCH,
  defaultCenter = [-1.5, 52.5],
  defaultZoom = 7,
  zoomAround = null,
  resetFitCoordinates = null,
  resetFitPadding = DEFAULT_RESET_FIT_PADDING,
}: MapCameraControlsProps) {
  const { map, isLoaded } = useMap();
  const [pitch, setPitch] = useState(0);
  const [bearing, setBearing] = useState(0);
  const compassDragRef = useRef<{
    pointerId: number;
    startAngle: number;
    startBearing: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;

    const syncCameraState = () => {
      setPitch(map.getPitch());
      setBearing(normalizeBearing(map.getBearing()));
    };

    syncCameraState();
    map.on('move', syncCameraState);
    map.on('moveend', syncCameraState);

    return () => {
      map.off('move', syncCameraState);
      map.off('moveend', syncCameraState);
    };
  }, [map, isLoaded]);

  const enabled3D = useMemo(() => is3DEnabled(pitch), [pitch]);

  useEffect(() => {
    if (!map || !isLoaded) return;

    if (enabled3D) {
      map.scrollZoom.disable();
      map.dragRotate.enable();
      map.touchZoomRotate.enable();
      map.touchZoomRotate.enableRotation();
      return () => {
        map.scrollZoom.enable();
      };
    }

    map.scrollZoom.enable();
  }, [enabled3D, isLoaded, map]);

  useEffect(() => {
    if (!map || !isLoaded || !enabled3D) return;

    const canvas = map.getCanvas();
    const handleWheel = (event: WheelEvent) => {
      const isPinchZoom = event.ctrlKey || event.metaKey;
      if (isPinchZoom) {
        event.preventDefault();
        const nextZoom = getNextZoomFromWheelDelta(
          map.getZoom(),
          event.deltaY,
          map.getMinZoom(),
          map.getMaxZoom(),
        );
        map.easeTo({
          ...buildZoomTransitionOptions(nextZoom, zoomAround),
          duration: 0,
          essential: true,
        });
        return;
      }

      const hasVertical = Math.abs(event.deltaY) > 0.01;
      const hasHorizontal = Math.abs(event.deltaX) > 0.01;
      if (!hasVertical && !hasHorizontal) return;

      event.preventDefault();
      const nextPitch = hasVertical
        ? getNextPitchFromWheelDelta(map.getPitch(), event.deltaY)
        : map.getPitch();
      const deltaForBearing = hasHorizontal
        ? event.deltaX
        : event.shiftKey && hasVertical
          ? event.deltaY
          : 0;
      const nextBearing = deltaForBearing !== 0
        ? getNextBearingFromWheelDelta(map.getBearing(), deltaForBearing)
        : map.getBearing();

      map.jumpTo({
        pitch: nextPitch,
        bearing: nextBearing,
      });

      setPitch(nextPitch);
      setBearing(normalizeBearing(nextBearing));
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [enabled3D, isLoaded, map, zoomAround]);

  const toggle3D = useCallback(() => {
    if (!map) return;
    map.easeTo({
      pitch: getNextPitchFor3DToggle(map.getPitch(), threeDPitch),
      duration: 350,
      essential: true,
    });
  }, [map, threeDPitch]);

  const zoomIn = useCallback(() => {
    if (!map) return;
    const nextZoom = Math.min(map.getZoom() + 1, map.getMaxZoom());
    map.easeTo({
      ...buildZoomTransitionOptions(nextZoom, zoomAround),
      duration: 220,
      essential: true,
    });
  }, [map, zoomAround]);

  const zoomOut = useCallback(() => {
    if (!map) return;
    const nextZoom = Math.max(map.getZoom() - 1, map.getMinZoom());
    map.easeTo({
      ...buildZoomTransitionOptions(nextZoom, zoomAround),
      duration: 220,
      essential: true,
    });
  }, [map, zoomAround]);

  const resetView = useCallback(() => {
    if (!map) return;

    const routeBounds = getCoordinateBounds(resetFitCoordinates);
    if (routeBounds) {
      const {
        minLng,
        maxLng,
        minLat,
        maxLat,
      } = routeBounds;
      const lngSpan = Math.abs(maxLng - minLng);
      const latSpan = Math.abs(maxLat - minLat);
      if (lngSpan < RESET_FIT_EPSILON && latSpan < RESET_FIT_EPSILON) {
        map.easeTo({
          center: [minLng, minLat],
          zoom: Math.max(map.getZoom(), 14),
          pitch: 0,
          bearing: 0,
          duration: 450,
          essential: true,
        });
        return;
      }

      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: resetFitPadding,
          duration: 450,
          essential: true,
          bearing: 0,
          pitch: 0,
        },
      );
      return;
    }

    map.easeTo({
      center: defaultCenter,
      zoom: defaultZoom,
      pitch: 0,
      bearing: 0,
      duration: 450,
      essential: true,
    });
  }, [
    map,
    defaultCenter,
    defaultZoom,
    resetFitCoordinates,
    resetFitPadding,
  ]);

  const locateMe = useCallback(() => {
    if (!map) return;
    if (!navigator.geolocation) {
      alert('Geolocation is not supported in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        map.easeTo({
          center: [coords.longitude, coords.latitude],
          zoom: Math.max(map.getZoom(), 14),
          duration: 500,
          essential: true,
        });
      },
      () => {
        alert('Unable to get your current location.');
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }, [map]);

  const resetNorth = useCallback(() => {
    if (!map) return;
    map.easeTo({
      bearing: 0,
      duration: 300,
      essential: true,
    });
  }, [map]);

  const pointerAngleFromCenter = useCallback((
    target: HTMLButtonElement,
    clientX: number,
    clientY: number,
  ) => {
    const rect = target.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    return (Math.atan2(clientY - centerY, clientX - centerX) * 180) / Math.PI;
  }, []);

  const handleCompassPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (!map) return;
    const startAngle = pointerAngleFromCenter(event.currentTarget, event.clientX, event.clientY);
    event.currentTarget.setPointerCapture(event.pointerId);
    compassDragRef.current = {
      pointerId: event.pointerId,
      startAngle,
      startBearing: map.getBearing(),
      moved: false,
    };
  }, [map, pointerAngleFromCenter]);

  const handleCompassPointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (!map) return;
    const drag = compassDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const currentAngle = pointerAngleFromCenter(event.currentTarget, event.clientX, event.clientY);
    const deltaAngle = getShortestSignedAngleDelta(drag.startAngle, currentAngle);
    if (Math.abs(deltaAngle) > 1) drag.moved = true;

    const nextBearing = normalizeBearing(drag.startBearing + deltaAngle);
    map.jumpTo({ bearing: nextBearing });
    setBearing(nextBearing);
  }, [map, pointerAngleFromCenter]);

  const handleCompassPointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = compassDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture(event.pointerId);
    const shouldReset = !drag.moved;
    compassDragRef.current = null;
    if (shouldReset) resetNorth();
  }, [resetNorth]);

  const setPitchFromSlider = useCallback((value: number) => {
    if (!map) return;
    const nextPitch = clampPitch(value);
    map.easeTo({
      pitch: nextPitch,
      duration: 120,
      essential: true,
    });
    setPitch(nextPitch);
  }, [map]);

  const compassDirection = useMemo(() => {
    const normalized = ((bearing % 360) + 360) % 360;
    const headings = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(normalized / 45) % headings.length;
    return headings[index];
  }, [bearing]);

  if (!map || !isLoaded) return null;

  return (
    <div className={cn('absolute right-3 top-20 z-30 flex flex-col items-center gap-2 pointer-events-auto', className)}>
      <div className="flex w-[2.4rem] flex-col items-center gap-1 rounded-[1rem] border border-white/60 bg-white/80 p-1 shadow-xl backdrop-blur dark:border-slate-600/70 dark:bg-slate-900/75">
        <div className="group relative">
          <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 rounded-full bg-slate-900/80 px-1.5 py-0.5 text-[8px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-slate-100 dark:text-slate-900">
            Reset
          </span>
          <button
            type="button"
            onClick={resetView}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100/90 text-slate-900 transition-colors hover:bg-slate-200 dark:bg-slate-700/80 dark:text-slate-100 dark:hover:bg-slate-600"
            title="Reset view"
            aria-label="Reset map view"
          >
            <GlobeIcon className="h-[0.9rem] w-[0.9rem]" />
          </button>
        </div>
        <button
          type="button"
          onClick={locateMe}
          className="flex h-6 w-6 items-center justify-center rounded-full text-primary-500 transition-colors hover:bg-primary-50 dark:hover:bg-slate-700"
          title="Locate me"
          aria-label="Locate me"
        >
          <NavigationArrowIcon className="h-[0.9rem] w-[0.9rem] -rotate-12" />
        </button>
      </div>

      <div className="flex w-[2.4rem] flex-col items-center rounded-[1rem] border border-white/60 bg-white/80 py-1 shadow-xl backdrop-blur dark:border-slate-600/70 dark:bg-slate-900/75">
        <button
          type="button"
          onClick={zoomIn}
          className="flex h-6 w-6 items-center justify-center rounded-full text-slate-900 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700"
          title="Zoom in"
          aria-label="Zoom in"
        >
          <PlusIcon className="h-[0.9rem] w-[0.9rem]" />
        </button>
        <div className="my-0.5 h-px w-5 bg-slate-300/70 dark:bg-slate-600/70" />
        <button
          type="button"
          onClick={zoomOut}
          className="flex h-6 w-6 items-center justify-center rounded-full text-slate-900 transition-colors hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-700"
          title="Zoom out"
          aria-label="Zoom out"
        >
          <MinusIcon className="h-[0.9rem] w-[0.9rem]" />
        </button>
      </div>

      <div className="flex w-[2.4rem] flex-col items-center gap-1 rounded-[1rem] border border-white/60 bg-white/80 p-1 shadow-xl backdrop-blur dark:border-slate-600/70 dark:bg-slate-900/75">
        <button
          type="button"
          onClick={toggle3D}
          className={cn(
            'h-6 w-[1.8rem] rounded-full text-[11px] font-semibold transition-colors',
            enabled3D
              ? 'bg-primary-500 text-white'
              : 'bg-slate-100/90 text-slate-900 hover:bg-slate-200 dark:bg-slate-700/80 dark:text-slate-100 dark:hover:bg-slate-600',
          )}
          aria-label={enabled3D ? 'Disable 3d view' : 'Enable 3d view'}
          title={enabled3D ? 'Switch to 2D view' : 'Switch to 3D view'}
        >
          {enabled3D ? '2D' : '3D'}
        </button>

        <div className="flex w-6 items-center justify-center rounded-full bg-slate-100/85 py-1.5 dark:bg-slate-700/70">
          <input
            type="range"
            min={0}
            max={85}
            step={1}
            value={Math.round(pitch)}
            onChange={(event) => setPitchFromSlider(Number(event.target.value))}
            className="h-[3.6rem] w-[0.9rem] cursor-ns-resize accent-slate-900 dark:accent-slate-100"
            style={{ writingMode: 'vertical-lr', direction: 'rtl' }}
            aria-label="Map pitch"
            title="Adjust 3D angle"
          />
        </div>
      </div>

      <button
        type="button"
        onPointerDown={handleCompassPointerDown}
        onPointerMove={handleCompassPointerMove}
        onPointerUp={handleCompassPointerUp}
        onPointerCancel={handleCompassPointerUp}
        className="flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full border border-white/60 bg-white/80 shadow-xl backdrop-blur transition-colors hover:bg-white dark:border-slate-600/70 dark:bg-slate-900/75 dark:hover:bg-slate-900"
        title="Reset to north"
        aria-label="Compass control: drag to rotate map, click to reset north"
      >
        <div className="relative flex h-6 w-6 items-center justify-center rounded-full border border-slate-300/70 bg-white/70 text-[11px] font-semibold text-slate-700 dark:border-slate-600/70 dark:bg-slate-800/80 dark:text-slate-100">
          <span className="absolute left-1/2 top-0.5 -translate-x-1/2 h-0 w-0 border-l-[2px] border-r-[2px] border-b-[4px] border-l-transparent border-r-transparent border-b-red-500" />
          {compassDirection}
        </div>
      </button>
    </div>
  );
}
